import {
  createContext,
  useContext,
  useReducer,
  useMemo,
  Dispatch,
  ReactNode
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { Campaign } from '../api/campaigns';

/** Which spreadsheet view is currently active on the campaign detail page. Only used for account campaigns. */
export type CampaignSpreadsheetViewType = 'account' | 'lead';

/** UI state for spreadsheet ↔ backend sync (debounced save), not CRM or other saves. */
export type SpreadsheetBackendSyncPhase = 'idle' | 'saving' | 'saved';

type CampaignState = {
  currentCampaign: Campaign | null;
  currentCampaignLoading: boolean;
  campaignId: string | null;
  /** Bumped when spreadsheet data is updated externally (e.g. CSV import) so the sheet can remount and re-fetch. */
  spreadsheetDataVersion: number;
  /** Whether the current user can edit the campaign (holds the lock or no one has it and we acquired it). */
  canEdit: boolean;
  /** Name of user holding the lock when locked by someone else. */
  lockedByUserName: string | null;
  /** Loading state for lock acquisition/check. */
  lockLoading: boolean;
  /** True while a CRM sync is in progress; spreadsheet is locked (read-only) during this time. */
  crmSyncActive: boolean;
  /** Debounced spreadsheet-to-backend sync indicator (see Spreadsheet / CampaignPageTopbar). */
  spreadsheetBackendSyncPhase: SpreadsheetBackendSyncPhase;
};

type Action =
  | { type: 'SET_CURRENT_CAMPAIGN'; value: Campaign | null }
  | { type: 'SET_CURRENT_CAMPAIGN_LOADING'; value: boolean }
  | { type: 'UPDATE_CURRENT_CAMPAIGN'; value: Partial<Campaign> }
  | { type: 'SET_CAMPAIGN_ID'; value: string | null }
  | { type: 'BUMP_SPREADSHEET_DATA_VERSION' }
  | {
      type: 'SET_CAMPAIGN_LOCK';
      value: { canEdit: boolean; lockedByUserName: string | null };
    }
  | { type: 'SET_LOCK_LOADING'; value: boolean }
  | { type: 'SET_CRM_SYNC_ACTIVE'; value: boolean }
  | {
      type: 'SET_SPREADSHEET_BACKEND_SYNC_PHASE';
      value: SpreadsheetBackendSyncPhase;
    };

const CampaignContext = createContext<
  [CampaignState, Dispatch<Action>] | undefined
>(undefined);

const initialState: CampaignState = {
  currentCampaign: null,
  currentCampaignLoading: false,
  campaignId: null,
  spreadsheetDataVersion: 0,
  canEdit: true,
  lockedByUserName: null,
  lockLoading: false,
  crmSyncActive: false,
  spreadsheetBackendSyncPhase: 'idle'
};

function reducer(state: CampaignState, action: Action): CampaignState {
  switch (action.type) {
    case 'SET_CURRENT_CAMPAIGN': {
      return { ...state, currentCampaign: action.value };
    }
    case 'SET_CURRENT_CAMPAIGN_LOADING': {
      return { ...state, currentCampaignLoading: action.value };
    }
    case 'UPDATE_CURRENT_CAMPAIGN': {
      return {
        ...state,
        currentCampaign: state.currentCampaign
          ? { ...state.currentCampaign, ...action.value }
          : null
      };
    }
    case 'SET_CAMPAIGN_ID': {
      return {
        ...state,
        campaignId: action.value,
        spreadsheetBackendSyncPhase: 'idle'
      };
    }
    case 'BUMP_SPREADSHEET_DATA_VERSION': {
      return {
        ...state,
        spreadsheetDataVersion: state.spreadsheetDataVersion + 1
      };
    }
    case 'SET_CAMPAIGN_LOCK': {
      return {
        ...state,
        canEdit: action.value.canEdit,
        lockedByUserName: action.value.lockedByUserName
      };
    }
    case 'SET_LOCK_LOADING': {
      return { ...state, lockLoading: action.value };
    }
    case 'SET_CRM_SYNC_ACTIVE': {
      return { ...state, crmSyncActive: action.value };
    }
    case 'SET_SPREADSHEET_BACKEND_SYNC_PHASE': {
      return { ...state, spreadsheetBackendSyncPhase: action.value };
    }
    default: {
      // @ts-ignore
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function CampaignContextProvider({ children }: { children: ReactNode }) {
  const reducerValue = useReducer(reducer, initialState);
  const value = useMemo(() => reducerValue, [reducerValue]);

  return (
    <CampaignContext.Provider value={value}>
      {children}
    </CampaignContext.Provider>
  );
}

function useCampaignContextController() {
  const context = useContext(CampaignContext);

  if (!context) {
    throw new Error(
      'useCampaignContextController should be used inside the CampaignContextProvider.'
    );
  }
  return context;
}

/** Returns the current campaign id from context (e.g. from URL on campaign detail page). */
function useCampaignId(): string | undefined {
  const [state] = useCampaignContextController();
  return state.campaignId ?? undefined;
}

const VIEW_PARAM = 'view';
const VALID_VIEW_TYPES: CampaignSpreadsheetViewType[] = ['account', 'lead'];

/** Returns the current spreadsheet view type (accounts vs leads) from URL search params. Defaults to 'account'. */
function useSpreadsheetViewType(): CampaignSpreadsheetViewType {
  const [searchParams] = useSearchParams();
  const viewParam = searchParams.get(VIEW_PARAM);
  if (
    viewParam &&
    VALID_VIEW_TYPES.includes(viewParam as CampaignSpreadsheetViewType)
  ) {
    return viewParam as CampaignSpreadsheetViewType;
  }
  return 'account';
}

/** Updates the spreadsheet view type in the URL. */
function useSetSpreadsheetViewType(): (
  viewType: CampaignSpreadsheetViewType
) => void {
  const [searchParams, setSearchParams] = useSearchParams();
  return (viewType: CampaignSpreadsheetViewType) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(VIEW_PARAM, viewType);
        return next;
      },
      { replace: true }
    );
  };
}

// Helper functions
const setCurrentCampaign = (
  dispatch: Dispatch<Action>,
  value: Campaign | null
) => dispatch({ type: 'SET_CURRENT_CAMPAIGN', value });
const setCurrentCampaignLoading = (
  dispatch: Dispatch<Action>,
  value: boolean
) => dispatch({ type: 'SET_CURRENT_CAMPAIGN_LOADING', value });
const setCampaignId = (dispatch: Dispatch<Action>, value: string | null) =>
  dispatch({ type: 'SET_CAMPAIGN_ID', value });
const updateCurrentCampaign = (
  dispatch: Dispatch<Action>,
  value: Partial<Campaign>
) => dispatch({ type: 'UPDATE_CURRENT_CAMPAIGN', value });
const bumpSpreadsheetDataVersion = (dispatch: Dispatch<Action>) =>
  dispatch({ type: 'BUMP_SPREADSHEET_DATA_VERSION' });
const setCampaignLock = (
  dispatch: Dispatch<Action>,
  value: { canEdit: boolean; lockedByUserName: string | null }
) => dispatch({ type: 'SET_CAMPAIGN_LOCK', value });
const setLockLoading = (dispatch: Dispatch<Action>, value: boolean) =>
  dispatch({ type: 'SET_LOCK_LOADING', value });
const setCrmSyncActive = (dispatch: Dispatch<Action>, value: boolean) =>
  dispatch({ type: 'SET_CRM_SYNC_ACTIVE', value });
const setSpreadsheetBackendSyncPhase = (
  dispatch: Dispatch<Action>,
  value: SpreadsheetBackendSyncPhase
) => dispatch({ type: 'SET_SPREADSHEET_BACKEND_SYNC_PHASE', value });

function useSpreadsheetBackendSyncPhase(): SpreadsheetBackendSyncPhase {
  const [state] = useCampaignContextController();
  return state.spreadsheetBackendSyncPhase;
}

export {
  CampaignContextProvider,
  useCampaignContextController,
  useCampaignId,
  useSpreadsheetViewType,
  useSetSpreadsheetViewType,
  useSpreadsheetBackendSyncPhase,
  setCurrentCampaign,
  setCurrentCampaignLoading,
  setCampaignId,
  updateCurrentCampaign,
  bumpSpreadsheetDataVersion,
  setCampaignLock,
  setLockLoading,
  setCrmSyncActive,
  setSpreadsheetBackendSyncPhase
};
